import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Download } from "lucide-react";
import externalAPI from "./ExternalAPI";
import { getUploadUrl, getImageRoot } from "../Utils/Utilities";
import { generateStoreQRCardDataUrl } from "../Utils/QRCodeGenerator";

export default function StoreQRCardView() {
  const [searchParams] = useSearchParams();
  const paramM = searchParams.get("m") || searchParams.get("agencyID") || searchParams.get("agnID") || searchParams.get("mobile");

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  const [cardDataUrl, setCardDataUrl] = useState(null);
  const [companyName, setCompanyName] = useState("Your Business");

  const getResolvedUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("data:") || path.startsWith("blob:")) return path;
    const cleanPath = path.replace(/^Uploads[/\\]/, "");
    return getUploadUrl(`Uploads/${cleanPath}`);
  };

  useEffect(() => {
    if (!paramM) {
      setLoading(false);
      setErrorMsg("Invalid URL parameter. Agency ID or Mobile number is missing.");
      return;
    }

    const loadAndGenerateCard = async () => {
      setLoading(true);
      setErrorMsg(null);
      try {
        const response = await externalAPI.getAgencyVitals(paramM);
        let updtPkg = response?.updatePackage;
        if (typeof updtPkg === "string") {
          try {
            updtPkg = JSON.parse(updtPkg);
          } catch (e) {
            console.error("Error parsing updatePackage:", e);
          }
        }

        let agencyInfo = null;
        if (updtPkg && updtPkg.Table1 && updtPkg.Table1.length > 0) {
          agencyInfo = updtPkg.Table1[0];
        }

        const name = agencyInfo?.AgencyName || agencyInfo?.agencyName || agencyInfo?.BizName || "Your Business";
        const rawLogo = agencyInfo?.Logo || agencyInfo?.LogoPath || agencyInfo?.logo;
        const logoUrl = rawLogo ? getResolvedUrl(rawLogo) : null;
        const sahiClubLogo = getImageRoot("Images", "SahiLogo.png");
        const subTypeID = Number(agencyInfo?.SubTypeID || 0);
        const isPremiumUser = subTypeID > 1;
        const webstoreUrl = `https://sahi.club/web/store?m=${paramM}`;

        setCompanyName(name);

        const dataUrl = await generateStoreQRCardDataUrl({
          text: webstoreUrl,
          companyName: name,
          logoUrl: logoUrl,
          sahiClubLogoUrl: sahiClubLogo,
          isPremiumUser: isPremiumUser,
        });

        setCardDataUrl(dataUrl);

        // Auto download after brief render on mobile/external browser
        try {
          const autoLink = document.createElement("a");
          autoLink.href = dataUrl;
          autoLink.download = `${name.replace(/[^a-zA-Z0-9]/g, "_")}_QR_Card.png`;
          document.body.appendChild(autoLink);
          autoLink.click();
          document.body.removeChild(autoLink);
        } catch (e) {
          console.warn("Auto-download triggered but handled gracefully by browser", e);
        }
      } catch (err) {
        console.error("Error generating QR card:", err);
        setErrorMsg("Failed to generate QR card. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadAndGenerateCard();
  }, [paramM]);

  const handleManualDownload = () => {
    if (!cardDataUrl) return;
    const link = document.createElement("a");
    link.href = cardDataUrl;
    link.download = `${companyName.replace(/[^a-zA-Z0-9]/g, "_")}_QR_Card.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#062f12] flex flex-col items-center justify-center p-4 text-white">
        <Loader2 className="h-10 w-10 animate-spin mb-4 text-white" />
        <p className="text-lg font-semibold tracking-wide">Generating your Business QR Card...</p>
        <p className="text-xs text-white/70 mt-1">Please wait a moment</p>
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center">
        <div className="bg-white rounded-2xl p-6 shadow-md max-w-sm w-full border border-gray-100">
          <p className="text-red-600 font-semibold mb-2">Notice</p>
          <p className="text-sm text-gray-700 mb-6">{errorMsg}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full py-2.5 bg-[#0b5d1e] text-white rounded-xl font-medium text-sm hover:bg-[#094418] transition-all"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#073815] flex flex-col items-center justify-between p-4 sm:p-6 select-none">
      {/* Title / Company Name */}
      <div className="w-full max-w-md flex items-center justify-center py-2 text-white">
        <h2 className="text-base font-bold tracking-wide truncate max-w-[320px] text-center">
          {companyName}
        </h2>
      </div>

      {/* Main Image Preview */}
      <div className="flex-1 flex flex-col items-center justify-center w-full max-w-md my-auto py-2">
        {cardDataUrl && (
          <div className="w-full flex items-center justify-center shadow-2xl rounded-2xl sm:rounded-3xl overflow-hidden">
            <img
              src={cardDataUrl}
              alt={`${companyName} QR Standee Card`}
              className="w-full h-auto max-h-[75vh] object-contain block rounded-2xl sm:rounded-3xl"
            />
          </div>
        )}
      </div>

      {/* Bottom Action Bar */}
      <div className="w-full max-w-md pt-3 pb-2">
        <button
          type="button"
          onClick={handleManualDownload}
          className="w-full py-3.5 px-4 rounded-xl bg-white text-[#0b5d1e] font-bold text-base shadow-xl hover:bg-gray-50 active:scale-98 transition-all flex items-center justify-center gap-2"
        >
          <Download className="h-5 w-5 stroke-[2.5]" />
          <span>Download QR Card</span>
        </button>
      </div>
    </div>
  );
}
